import { useState, useEffect, useMemo } from 'react';
import { gastosApi, parametrosApi } from '../../utils/api';
import {
  Truck, Search, FileText, AlertCircle,
  DollarSign, Banknote, X, Check, Building2, Eye, CreditCard, Download, FileSpreadsheet, RefreshCw
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import DraggableModal from '../../components/DraggableModal';
import NotificarPagoProveedorModal from '../../components/NotificarPagoProveedorModal';
import useAuthStore from '../../store/authStore';
import useToolbarResponsive from '../../hooks/useToolbarResponsive';

export default function PendientesPorProveedor() {
  const { currentEmisor } = useAuthStore();
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchProveedor, setSearchProveedor] = useState('');
  const [selectedProveedorId, setSelectedProveedorId] = useState(null);
  const [bancos, setBancos] = useState([]);
  // Hook responsive: empuja los botones Excel/PDF a la derecha si el nombre del proveedor (absolute) los traslapa.
  const { searchRef: providerNameRef, buttonsRef: exportButtonsRef } = useToolbarResponsive([selectedProveedorId]);

  // Notificación de pago al proveedor (modal automático tras aplicar pago)
  const [notifPagoData, setNotifPagoData] = useState(null);

  // Modal de pago
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);

  // Pago múltiple
  const [selectedFacturas, setSelectedFacturas] = useState([]);
  const [showPagoMultipleModal, setShowPagoMultipleModal] = useState(false);

  // Aplicar NC
  const [showAplicarNCModal, setShowAplicarNCModal] = useState(false);
  const [selectedNC, setSelectedNC] = useState(null);
  const [selectedFENCIds, setSelectedFENCIds] = useState([]); // FE seleccionadas para aplicar la NC (multi)
  const [ncNotas, setNCNotas] = useState('');
  const [aplicandoNC, setAplicandoNC] = useState(false);

  const [pagoData, setPagoData] = useState({
    monto: '',
    fechaPago: dayjs().format('YYYY-MM-DD'),
    medioPago: 'transferencia',
    numeroComprobante: '',
    banco: '',
    notas: ''
  });
  const [pagarEnColones, setPagarEnColones] = useState(false);
  const [montoCRC, setMontoCRC] = useState('');
  const [pagoFile, setPagoFile] = useState(null);
  const [aplicandoPago, setAplicandoPago] = useState(false);

  // Modal de vista previa PDF
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFactura, setPreviewFactura] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    loadFacturas();
    loadBancos();
  }, []);

  const loadBancos = async () => {
    try {
      const response = await parametrosApi.listBancosActivos();
      setBancos(response.data.data || []);
    } catch (error) {
      console.error('Error loading bancos:', error);
    }
  };

  const loadFacturas = async () => {
    try {
      setLoading(true);
      // Cargar facturas por pagar (el backend ya incluye las parciales automáticamente)
      const response = await gastosApi.listFacturas({ estado: 'por_pagar', limit: 500 });
      setFacturas(response.data.data || []);
    } catch (error) {
      console.error('Error loading facturas:', error);
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  // Agrupar facturas por proveedor
  const proveedoresConFacturas = useMemo(() => {
    const proveedorMap = new Map();

    facturas.forEach(f => {
      const proveedorKey = f.proveedorId || f.proveedorIdentificacion || 'sin-proveedor';

      if (!proveedorMap.has(proveedorKey)) {
        proveedorMap.set(proveedorKey, {
          id: proveedorKey,
          nombre: f.proveedorNombre || 'Proveedor sin nombre',
          identificacion: f.proveedorIdentificacion || '',
          facturas: [],
          totalCRC: 0,
          totalUSD: 0,
          cantidadCRC: 0,
          cantidadUSD: 0,
          vencidas: 0,
          tieneVencidas: false
        });
      }

      const proveedor = proveedorMap.get(proveedorKey);
      proveedor.facturas.push(f);

      const saldo = parseFloat(f.saldoPendiente) || 0;
      if (f.moneda === 'USD') {
        proveedor.totalUSD += saldo;
        proveedor.cantidadUSD++;
      } else {
        proveedor.totalCRC += saldo;
        proveedor.cantidadCRC++;
      }

      // Verificar si está vencida
      if (f.fechaVencimiento && dayjs(f.fechaVencimiento).isBefore(dayjs(), 'day')) {
        proveedor.vencidas++;
        proveedor.tieneVencidas = true;
      }
    });

    // Convertir a array y ordenar por nombre alfabéticamente
    return Array.from(proveedorMap.values())
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [facturas]);

  // Filtrar proveedores por búsqueda
  const proveedoresFiltrados = useMemo(() => {
    if (!searchProveedor.trim()) return proveedoresConFacturas;
    const search = searchProveedor.toLowerCase();
    return proveedoresConFacturas.filter(p =>
      p.nombre.toLowerCase().includes(search) ||
      p.identificacion.toLowerCase().includes(search)
    );
  }, [proveedoresConFacturas, searchProveedor]);

  // Proveedor seleccionado
  const proveedorSeleccionado = useMemo(() => {
    if (!selectedProveedorId) return null;
    return proveedoresConFacturas.find(p => p.id === selectedProveedorId);
  }, [proveedoresConFacturas, selectedProveedorId]);

  // Seleccionar primer proveedor por defecto
  useEffect(() => {
    if (proveedoresFiltrados.length > 0 && !selectedProveedorId) {
      setSelectedProveedorId(proveedoresFiltrados[0].id);
    }
  }, [proveedoresFiltrados, selectedProveedorId]);

  // Reset selected facturas when provider changes
  useEffect(() => {
    setSelectedFacturas([]);
  }, [selectedProveedorId]);

  // Toggle factura selection
  const toggleFacturaSelection = (factura) => {
    setSelectedFacturas(prev => {
      const isSelected = prev.some(f => f.id === factura.id);
      if (isSelected) {
        return prev.filter(f => f.id !== factura.id);
      } else {
        // Only allow same currency selection
        if (prev.length > 0 && prev[0].moneda !== factura.moneda) {
          toast.error('Solo puede seleccionar facturas de la misma moneda');
          return prev;
        }
        return [...prev, factura];
      }
    });
  };

  // Select all facturas of same currency
  const toggleSelectAll = (moneda) => {
    if (!proveedorSeleccionado) return;
    const facturasMoneda = proveedorSeleccionado.facturas.filter(f => f.moneda === moneda);
    const allSelected = facturasMoneda.every(f => selectedFacturas.some(sf => sf.id === f.id));

    if (allSelected) {
      setSelectedFacturas(prev => prev.filter(f => f.moneda !== moneda));
    } else {
      setSelectedFacturas(facturasMoneda);
    }
  };

  // Calculate total selected. Redondear cada saldo a céntimos (dinero) antes de sumar:
  // varias FE tienen saldos con sub-céntimos y sumar los crudos da 1 céntimo menos que
  // lo que realmente se paga (suma de saldos redondeados por FE).
  const totalSeleccionado = useMemo(() => {
    return selectedFacturas.reduce((sum, f) => sum + Math.round((parseFloat(f.saldoPendiente) || 0) * 100) / 100, 0);
  }, [selectedFacturas]);

  const formatMoney = (amount, moneda = 'CRC') => {
    const num = parseFloat(amount) || 0;
    if (moneda === 'USD') {
      return `$ ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₡ ${num.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return dayjs(dateStr).format('DD/MM/YYYY');
  };

  const getDiasVencido = (fechaVencimiento) => {
    if (!fechaVencimiento) return null;
    const hoy = dayjs().startOf('day');
    const vence = dayjs(fechaVencimiento).startOf('day');
    return hoy.diff(vence, 'day');
  };

  const openPagoModal = (factura) => {
    setSelectedFactura(factura);
    setPagoData({
      monto: factura.saldoPendiente?.toFixed(2) || '',
      fechaPago: dayjs().format('YYYY-MM-DD'),
      medioPago: 'transferencia',
      numeroComprobante: '',
      banco: '',
      notas: ''
    });
    setPagoFile(null);
    setShowPagoModal(true);
  };

  const openPreviewModal = async (factura) => {
    setPreviewFactura(factura);
    setShowPreviewModal(true);
    setPdfUrl(null);

    if (factura.hasPdf) {
      try {
        setPdfLoading(true);
        const response = await gastosApi.downloadPDF(factura.id);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (error) {
        console.error('Error loading PDF:', error);
      } finally {
        setPdfLoading(false);
      }
    }
  };

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewFactura(null);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleAplicarPago = async (e) => {
    e?.preventDefault();
    if (!selectedFactura) return;

    const monto = parseFloat(pagoData.monto);
    if (isNaN(monto) || monto <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }

    if (monto > selectedFactura.saldoPendiente + 0.01) {
      toast.error('El monto excede el saldo pendiente');
      return;
    }

    if (!pagoFile) {
      toast.error('Debe adjuntar el comprobante de pago');
      return;
    }

    try {
      setAplicandoPago(true);

      const data = new FormData();
      data.append('fechaPago', pagoData.fechaPago);
      data.append('monto', monto);
      data.append('moneda', selectedFactura.moneda || 'CRC');
      data.append('medioPago', pagoData.medioPago);
      data.append('numeroComprobante', pagoData.numeroComprobante || '');
      data.append('banco', pagoData.banco || '');
      data.append('comprobante', pagoFile);
      if (pagarEnColones && selectedFactura.moneda === 'USD' && montoCRC) {
        data.append('pagarEnColones', 'true');
        data.append('montoCRC', montoCRC);
      }

      const res = await gastosApi.aplicarPago(selectedFactura.id, data);

      toast.success('Pago aplicado exitosamente');

      // Construir info para el modal de notificación al proveedor
      const respData = res?.data?.data || {};
      const notifInfo = {
        parentId: null,
        pagoId: respData.pago?.id || null,
        proveedorId: selectedFactura.proveedorId,
        proveedorNombre: selectedFactura.proveedorNombre || '',
        moneda: selectedFactura.moneda || 'CRC',
        montoTotal: monto,
        fechaPago: pagoData.fechaPago,
        medioPago: pagoData.medioPago,
        banco: pagoData.banco,
        numeroComprobante: pagoData.numeroComprobante,
        comprobanteFilename: pagoFile?.name || null,
        facturasCount: 1
      };

      setShowPagoModal(false);
      setSelectedFactura(null);
      setSelectedFacturas([]); // limpiar la barra amarilla de "Pago individual"
      loadFacturas();
      if (notifInfo.proveedorId) setNotifPagoData(notifInfo);
    } catch (error) {
      console.error('Error aplicando pago:', error);
      toast.error(error.response?.data?.error || 'Error al aplicar pago');
    } finally {
      setAplicandoPago(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Layout Master-Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[var(--alto-contenido)]">

        {/* Panel Izquierdo - Lista de Proveedores */}
        <div className="lg:col-span-1 bg-white rounded-xl border flex flex-col overflow-hidden">
          {/* Búsqueda */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar proveedor..."
                value={searchProveedor}
                onChange={(e) => setSearchProveedor(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>

          {/* Lista de Proveedores */}
          <div className="flex-1 overflow-y-auto">
            {proveedoresFiltrados.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Truck className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                <p>No hay proveedores con facturas pendientes</p>
              </div>
            ) : (
              <div className="divide-y">
                {proveedoresFiltrados.map((proveedor) => (
                  <button
                    key={proveedor.id}
                    onClick={() => setSelectedProveedorId(proveedor.id)}
                    className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${
                      selectedProveedorId === proveedor.id ? 'bg-orange-50 border-l-4 border-l-orange-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{proveedor.nombre}</p>
                        <p className="text-xs text-gray-500">{proveedor.identificacion}</p>
                      </div>
                      {proveedor.tieneVencidas && (
                        <span className="flex-shrink-0 px-1.5 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                          {proveedor.vencidas} venc.
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer con conteo */}
          <div className="px-3 py-2 border-t bg-gray-50 text-xs text-gray-500 text-center font-medium">
            {proveedoresFiltrados.length} proveedor{proveedoresFiltrados.length !== 1 ? 'es' : ''}
          </div>

        </div>

        {/* Panel Derecho - Detalle del Proveedor */}
        <div className="lg:col-span-2 bg-white rounded-xl border flex flex-col overflow-hidden">
          {!proveedorSeleccionado ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Truck className="h-16 w-16 mx-auto text-gray-300 mb-3" />
                <p className="font-medium">Seleccione un proveedor</p>
                <p className="text-sm">para ver sus facturas pendientes</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header del proveedor — responsive: si el nombre es largo, los botones se desplazan a la derecha */}
              <div className="p-4 border-b bg-white relative">
                <div ref={providerNameRef} className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-3 z-10">
                  <div className="p-3 bg-gray-100 rounded-xl">
                    <Building2 className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">{proveedorSeleccionado.nombre}</h2>
                    <p className="text-sm text-gray-500">{proveedorSeleccionado.identificacion}</p>
                  </div>
                </div>
                <div ref={exportButtonsRef} className="flex items-center justify-center gap-2 py-2">
                    <button
                      onClick={async () => {
                        try {
                          const pid = proveedorSeleccionado.facturas?.[0]?.proveedorId || proveedorSeleccionado.id;
                          const res = await gastosApi.exportExcel({ estado: 'por_pagar', proveedorId: pid });
                          const url = URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement('a'); a.href = url;
                          a.download = `CxP_${proveedorSeleccionado.nombre.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
                          a.click(); URL.revokeObjectURL(url);
                          toast.success('Excel exportado');
                        } catch (e) { toast.error('Error al exportar Excel'); }
                      }}
                      className="flex flex-col items-center px-3 py-1.5 rounded-lg border border-blue-300 text-blue-600 hover:bg-blue-50 text-[10px] font-medium"
                    >
                      <FileSpreadsheet size={16} />
                      Excel
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const pid = proveedorSeleccionado.facturas?.[0]?.proveedorId || proveedorSeleccionado.id;
                          const res = await gastosApi.exportPdf({ estado: 'por_pagar', proveedorId: pid });
                          const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                          const a = document.createElement('a'); a.href = url;
                          a.download = `CxP_${proveedorSeleccionado.nombre.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.pdf`;
                          a.click(); URL.revokeObjectURL(url);
                          toast.success('PDF exportado');
                        } catch (e) { toast.error('Error al exportar PDF'); }
                      }}
                      className="flex flex-col items-center px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-[10px] font-medium"
                    >
                      <Download size={16} />
                      PDF
                    </button>
                  </div>
              </div>

              {/* Cards de Totales */}
              <div className="p-4 border-b">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {/* CRC */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Banknote className="h-4 w-4 text-orange-500" />
                      <span className="text-xs font-medium text-gray-700">Colones</span>
                    </div>
                    <p className="text-lg font-bold text-orange-600 ">
                      {formatMoney(proveedorSeleccionado.totalCRC, 'CRC')}
                    </p>
                    <p className="text-xs text-gray-500">{proveedorSeleccionado.cantidadCRC} fact.</p>
                  </div>

                  {/* USD */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="h-4 w-4 text-blue-500" />
                      <span className="text-xs font-medium text-gray-700">Dólares</span>
                    </div>
                    <p className="text-lg font-bold text-blue-600 ">
                      {formatMoney(proveedorSeleccionado.totalUSD, 'USD')}
                    </p>
                    <p className="text-xs text-gray-500">{proveedorSeleccionado.cantidadUSD} fact.</p>
                  </div>

                  {/* Vencidas */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className={`h-4 w-4 ${proveedorSeleccionado.vencidas > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                      <span className="text-xs font-medium text-gray-700">Vencidas</span>
                    </div>
                    <p className={`text-lg font-bold  ${proveedorSeleccionado.vencidas > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                      {proveedorSeleccionado.vencidas}
                    </p>
                    <p className={`text-xs ${proveedorSeleccionado.vencidas > 0 ? 'text-red-500' : 'text-gray-400'}`}>
                      {proveedorSeleccionado.vencidas > 0 ? 'Requiere atención' : 'Al día'}
                    </p>
                  </div>

                  {/* Total Facturas */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-purple-500" />
                      <span className="text-xs font-medium text-gray-700">Total</span>
                    </div>
                    <p className="text-lg font-bold text-purple-600 ">
                      {proveedorSeleccionado.facturas.length}
                    </p>
                    <p className="text-xs text-gray-500">Facturas pend.</p>
                  </div>
                </div>
              </div>

              {/* Barra de Pago — SIEMPRE presente con altura fija. Antes solo se
                  renderizaba al seleccionar, lo que encogía la tabla (altura fija del
                  panel) y movía el scroll, "perdiendo" la fila recién seleccionada. */}
              <div className={`px-4 py-3 border-b flex items-center justify-between min-h-[60px] ${selectedFacturas.length > 0 ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-100'}`}>
                {selectedFacturas.length > 0 ? (
                  <>
                    <div className="flex items-center gap-3">
                      {selectedFacturas.length === 1 ? (
                        <span className="text-sm font-medium text-orange-800">
                          Pago individual: <strong>{selectedFacturas[0].fecNumeroFacturaProveedor || selectedFacturas[0].numeroConsecutivo || selectedFacturas[0].consecutivo}</strong>
                        </span>
                      ) : (
                        <span className="text-sm font-medium text-orange-800">
                          Pago Múltiple de {selectedFacturas.length} facturas para <strong>{proveedorSeleccionado?.nombre}</strong>
                        </span>
                      )}
                      <span className="text-sm text-orange-600">
                        Total: <strong>{formatMoney(totalSeleccionado, selectedFacturas[0]?.moneda)}</strong>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedFacturas([])}
                        className="px-4 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100"
                      >
                        Cancelar
                      </button>
                      {selectedFacturas.length === 1 && (selectedFacturas[0].tipoDocumento === '03' || selectedFacturas[0].tipoDocumento === 'NC') ? (
                        <button
                          onClick={() => {
                            setSelectedNC(selectedFacturas[0]);
                            setSelectedFENCIds([]);
                            setNCNotas('');
                            setShowAplicarNCModal(true);
                          }}
                          className="px-4 py-1.5 text-sm font-medium border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 flex items-center gap-2"
                        >
                          <FileText className="h-4 w-4" />
                          Aplicar NC
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            if (selectedFacturas.length === 1) {
                              openPagoModal(selectedFacturas[0]);
                            } else {
                              setShowPagoMultipleModal(true);
                            }
                          }}
                          className="px-4 py-1.5 text-sm font-medium border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 flex items-center gap-2"
                        >
                          <CreditCard className="h-4 w-4" />
                          Aplicar Pago
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <span className="text-sm text-gray-400">Seleccione una o más facturas para registrar un pago</span>
                )}
              </div>

              {/* Tabla de Facturas */}
              <div className="flex-1 overflow-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-center w-10">
                        <input
                          type="checkbox"
                          checked={proveedorSeleccionado?.facturas.length > 0 &&
                            proveedorSeleccionado.facturas.every(f => selectedFacturas.some(sf => sf.id === f.id))}
                          onChange={() => {
                            if (proveedorSeleccionado?.facturas.length > 0) {
                              const moneda = proveedorSeleccionado.facturas[0]?.moneda || 'CRC';
                              toggleSelectAll(moneda);
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Tipo Doc.</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">No. Documento</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Emisión</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Vencimiento</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Estado</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Pagado</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Saldo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {proveedorSeleccionado.facturas
                      .sort((a, b) => new Date(a.fechaVencimiento) - new Date(b.fechaVencimiento))
                      .map((factura) => {
                        const diasVencido = getDiasVencido(factura.fechaVencimiento);
                        const estaVencida = diasVencido !== null && diasVencido > 0;
                        const isSelected = selectedFacturas.some(sf => sf.id === factura.id);

                        return (
                          <tr key={factura.id} className={`hover:bg-gray-50 ${estaVencida ? 'bg-red-50/50' : ''} ${isSelected ? 'bg-orange-50' : ''}`}>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleFacturaSelection(factura)}
                                className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {factura.tipoDocumento || 'FE'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => openPreviewModal(factura)}
                                  className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                  title="Ver factura"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <span className="font-medium text-gray-900">{factura.fecNumeroFacturaProveedor || factura.numeroConsecutivo || factura.numeroFactura || factura.consecutivo || '-'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {formatDate(factura.fechaEmision || factura.fechaFactura)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {formatDate(factura.fechaVencimiento)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {(() => {
                                // Si tiene pago parcial, mostrar ese estado primero
                                if ((factura.totalPagado || 0) > 0) {
                                  return (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                                      Pago Parcial
                                    </span>
                                  );
                                }

                                const dias = diasVencido !== null ? -diasVencido : null;
                                if (dias === null) return <span className="text-gray-400 text-xs">-</span>;
                                if (dias > 10) {
                                  return <span className="whitespace-nowrap px-2 py-0.5 text-xs border border-green-300 text-green-700 rounded">Vence {dias}d</span>;
                                } else if (dias > 1) {
                                  return <span className="whitespace-nowrap px-2 py-0.5 text-xs border border-yellow-300 text-yellow-700 rounded">Vence {dias}d</span>;
                                } else if (dias === 1) {
                                  return <span className="whitespace-nowrap px-2 py-0.5 text-xs border border-orange-300 text-orange-700 rounded">Mañana</span>;
                                } else if (dias === 0) {
                                  return <span className="whitespace-nowrap px-2 py-0.5 text-xs font-medium border border-red-300 text-red-700 rounded">Hoy</span>;
                                } else {
                                  return <span className="whitespace-nowrap px-2 py-0.5 text-xs font-medium border border-red-400 text-red-700 rounded">Venció {Math.abs(dias)}d</span>;
                                }
                              })()}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              {formatMoney(factura.totalComprobante, factura.moneda)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {(factura.totalPagado || 0) > 0 ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                  {formatMoney(factura.totalPagado, factura.moneda)}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`text-sm font-bold ${
                                factura.moneda === 'USD' ? 'text-blue-600' : 'text-gray-600'
                              }`}>
                                {formatMoney(factura.saldoPendiente, factura.moneda)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de Pago */}
      {showPagoModal && selectedFactura && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-green-600 to-green-700 rounded-t-xl">
              <h3 className="text-lg font-semibold text-white">Pago a Proveedor</h3>
              <button onClick={() => setShowPagoModal(false)} className="text-white/80 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {/* Invoice Summary */}
            <div className="px-6 py-4 bg-gray-50 border-b">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{proveedorSeleccionado?.nombre}</p>
                  <p className="text-sm text-gray-500 font-mono mt-0.5">{selectedFactura.fecNumeroFacturaProveedor || selectedFactura.numeroConsecutivo || selectedFactura.numeroFactura || '-'}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Total a Pagar</p>
                  <p className="text-2xl font-bold text-green-700 ">
                    {formatMoney(selectedFactura.saldoPendiente, selectedFactura.moneda)}
                  </p>
                </div>
              </div>

              {/* Single invoice details */}
              <div className="mt-3 flex gap-6 text-sm">
                <div>
                  <span className="text-gray-500">Total factura:</span>
                  <span className="ml-2 text-sm font-medium">{formatMoney(selectedFactura.totalComprobante, selectedFactura.moneda)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Ya pagado:</span>
                  <span className="ml-2 text-sm font-medium">{formatMoney(selectedFactura.totalPagado || 0, selectedFactura.moneda)}</span>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleAplicarPago} className="p-6">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de Pago *
                  </label>
                  <input
                    type="date"
                    value={pagoData.fechaPago}
                    onChange={(e) => setPagoData({ ...pagoData, fechaPago: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monto a Pagar *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={pagoData.monto}
                    onChange={(e) => setPagoData({ ...pagoData, monto: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                  <div className="flex gap-1 mt-1">
                    {[25, 50, 75, 100].map(pct => (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => setPagoData({ ...pagoData, monto: ((selectedFactura.saldoPendiente || 0) * pct / 100).toFixed(2) })}
                        className="flex-1 text-xs py-1 border border-gray-300 rounded hover:bg-green-50 hover:border-green-400 hover:text-green-700 transition-colors font-medium text-gray-600"
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Saldo Restante
                  </label>
                  <div className={`w-full border rounded-lg px-3 py-2  font-semibold ${
                    (selectedFactura.saldoPendiente - (parseFloat(pagoData.monto) || 0)) <= 0.01
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                  }`}>
                    {formatMoney(Math.max(0, (selectedFactura.saldoPendiente || 0) - (parseFloat(pagoData.monto) || 0)), selectedFactura.moneda)}
                  </div>
                </div>
              </div>

              {/* Pagar en Colones (solo para facturas USD) */}
              {selectedFactura.moneda === 'USD' && (
                <div className="mt-4 p-3 border border-amber-200 rounded-lg bg-amber-50">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pagarEnColones}
                      onChange={(e) => { setPagarEnColones(e.target.checked); if (!e.target.checked) setMontoCRC(''); }}
                      className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-sm font-medium text-amber-800">Pagar en Colones (₡)</span>
                  </label>
                  {pagarEnColones && (
                    <div className="mt-3 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Deuda original (CRC)</label>
                          <div className="px-3 py-2 bg-white border rounded-lg text-sm font-mono text-gray-700">
                            ₡{((parseFloat(pagoData.monto) || 0) * (selectedFactura.tipoCambio || 1)).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <p className="text-[10px] text-gray-500 mt-0.5">T/C factura: ₡{selectedFactura.tipoCambio?.toFixed(2)}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Monto pagado en ₡ *</label>
                          <input
                            type="number"
                            step="0.01"
                            value={montoCRC}
                            onChange={(e) => setMontoCRC(e.target.value)}
                            placeholder="Monto real en colones"
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            required={pagarEnColones}
                          />
                        </div>
                      </div>
                      {montoCRC && pagoData.monto && (() => {
                        const deudaCRC = (parseFloat(pagoData.monto) || 0) * (selectedFactura.tipoCambio || 1);
                        const pagoCRC = parseFloat(montoCRC) || 0;
                        const diferencial = pagoCRC - deudaCRC;
                        const esPerdida = diferencial > 0;
                        const tcPago = pagoCRC / (parseFloat(pagoData.monto) || 1);
                        return (
                          <div className={`p-2 rounded-lg border text-sm ${esPerdida ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">T/C del pago:</span>
                              <span className="font-mono font-medium">₡{tcPago.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center mt-1">
                              <span className={`font-medium ${esPerdida ? 'text-red-700' : 'text-green-700'}`}>
                                {esPerdida ? 'Pérdida' : 'Ganancia'} por diferencial cambiario:
                              </span>
                              <span className={`font-bold font-mono ${esPerdida ? 'text-red-700' : 'text-green-700'}`}>
                                {esPerdida ? '' : '-'}₡{Math.abs(diferencial).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Medio de Pago
                  </label>
                  <select
                    value={pagoData.medioPago}
                    onChange={(e) => setPagoData({ ...pagoData, medioPago: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="cheque">Cheque</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    No. Comprobante
                  </label>
                  <input
                    type="text"
                    value={pagoData.numeroComprobante}
                    onChange={(e) => setPagoData({ ...pagoData, numeroComprobante: e.target.value })}
                    placeholder="Referencia"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Banco
                  </label>
                  <select
                    value={pagoData.banco}
                    onChange={(e) => setPagoData({ ...pagoData, banco: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Seleccionar banco</option>
                    {bancos.map(banco => (
                      <option key={banco.id} value={banco.nombre}>
                        {banco.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comprobante *
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setPagoFile(e.target.files?.[0] || null)}
                  className="w-full border rounded-lg px-2 py-1.5 text-sm file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                  required
                />
                {!pagoFile && (
                  <p className="text-xs text-amber-600 mt-1">Debe adjuntar el comprobante de pago</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowPagoModal(false)}
                  className="px-5 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={aplicandoPago || !pagoFile}
                  className="px-6 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 font-medium flex items-center gap-2"
                >
                  {aplicandoPago ? (
                    <>
                      <span className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></span>
                      Aplicando...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Pagar Factura
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Vista Previa PDF */}
      {showPreviewModal && previewFactura && (
        <DraggableModal
          title={`${proveedorSeleccionado?.nombre} - ${previewFactura.fecNumeroFacturaProveedor || previewFactura.numeroConsecutivo}`}
          onClose={closePreviewModal}
        >
          <div className="w-full h-full bg-gray-200">
            {pdfLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <p className="text-gray-600">Cargando factura...</p>
                </div>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl}
                className="w-full h-full"
                title={`Factura ${previewFactura.numeroConsecutivo}`}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md">
                  <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">PDF no disponible</h3>
                  <p className="text-sm text-gray-500 mb-4">Esta factura no tiene un PDF asociado.</p>
                  <div className="text-left bg-gray-50 rounded-lg p-4 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <span className="text-gray-500">Proveedor:</span>
                      <span className="font-medium">{proveedorSeleccionado?.nombre}</span>
                      <span className="text-gray-500">Factura:</span>
                      <span className="font-medium">{previewFactura.fecNumeroFacturaProveedor || previewFactura.numeroConsecutivo}</span>
                      <span className="text-gray-500">Fecha:</span>
                      <span className="font-medium">{formatDate(previewFactura.fechaEmision)}</span>
                      <span className="text-gray-500">Total:</span>
                      <span className="font-medium">{formatMoney(previewFactura.totalComprobante, previewFactura.moneda)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DraggableModal>
      )}

      {/* Modal Aplicar NC (multi-selección: reparte la NC entre varias FE) */}
      {showAplicarNCModal && selectedNC && proveedorSeleccionado && (() => {
        const creditoNC = (parseFloat(selectedNC.saldoPendiente) > 0)
          ? parseFloat(selectedNC.saldoPendiente)
          : (parseFloat(selectedNC.totalComprobante) || 0);
        const facturasDisponibles = proveedorSeleccionado.facturas
          .filter(f => (f.tipoDocumento === '01' || f.tipoDocumento === 'FE' || f.tipoDocumento === 'FEC') && (f.saldoPendiente || 0) > 0)
          .sort((a, b) => new Date(a.fechaEmision || 0) - new Date(b.fechaEmision || 0));

        // Reparto en orden (más antigua primero) hasta agotar el crédito de la NC
        const seleccionadas = facturasDisponibles.filter(f => selectedFENCIds.includes(f.id));
        let restante = creditoNC;
        let totalAplicar = 0;
        const distMap = {};
        for (const f of seleccionadas) {
          const saldo = parseFloat(f.saldoPendiente) || 0;
          const aplicar = Math.max(0, Math.round(Math.min(restante, saldo) * 100) / 100);
          distMap[f.id] = aplicar;
          restante = Math.round((restante - aplicar) * 100) / 100;
          totalAplicar = Math.round((totalAplicar + aplicar) * 100) / 100;
        }
        const sobranteNC = Math.max(0, Math.round((creditoNC - totalAplicar) * 100) / 100);
        const toggleFE = (id) => setSelectedFENCIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        const cerrar = () => { setShowAplicarNCModal(false); setSelectedNC(null); setSelectedFENCIds([]); setNCNotas(''); };

        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={cerrar}>
            <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-purple-700 rounded-t-xl flex items-center justify-between shrink-0">
                <h3 className="text-lg font-semibold text-white">Aplicar Nota de Crédito</h3>
                <button onClick={cerrar} className="text-white/80 hover:text-white"><X size={20} /></button>
              </div>

              <div className="px-6 py-4 bg-purple-50 border-b shrink-0">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-gray-900">{proveedorSeleccionado.nombre}</p>
                    <p className="text-sm text-gray-500 font-mono mt-0.5">{selectedNC.fecNumeroFacturaProveedor || selectedNC.numeroConsecutivo}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Crédito NC</p>
                    <p className="text-xl font-bold text-purple-700">{formatMoney(creditoNC, selectedNC.moneda)}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                <p className="text-sm font-medium text-gray-700 mb-3">Seleccione las facturas a saldar (se aplican en orden hasta agotar la NC):</p>
                {facturasDisponibles.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No hay facturas pendientes de este proveedor</p>
                ) : (
                  <div className="space-y-2">
                    {facturasDisponibles.map(f => {
                      const sel = selectedFENCIds.includes(f.id);
                      const aplicado = distMap[f.id] || 0;
                      const saldoActual = parseFloat(f.saldoPendiente) || 0;
                      const saldoFinal = Math.max(0, Math.round((saldoActual - aplicado) * 100) / 100);
                      const quedaSaldo = sel && aplicado > 0 && saldoFinal > 0.01;
                      const quedaSaldada = sel && aplicado > 0 && saldoFinal <= 0.01;
                      return (
                        <label key={f.id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${sel ? 'bg-purple-50 border-purple-300' : 'hover:bg-gray-50'}`}>
                          <input type="checkbox" checked={sel} onChange={() => toggleFE(f.id)} className="accent-purple-600 w-4 h-4" />
                          <div className="flex-1">
                            <p className="font-mono text-sm text-gray-900">{f.fecNumeroFacturaProveedor || f.numeroConsecutivo}</p>
                            <p className="text-xs text-gray-500">{dayjs(f.fechaEmision).format('DD/MM/YYYY')} — Saldo: {formatMoney(f.saldoPendiente, f.moneda)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] text-gray-400">A aplicar</p>
                            <p className={`text-sm font-bold ${sel && aplicado > 0 ? 'text-purple-700' : 'text-gray-300'}`}>{sel && aplicado > 0 ? formatMoney(aplicado, f.moneda) : '-'}</p>
                            {/* Saldo después de aplicar — solo se muestra cuando hay aplicación */}
                            {(quedaSaldo || quedaSaldada) && (
                              <p className={`text-[11px] mt-1 ${quedaSaldada ? 'text-emerald-600 font-semibold' : 'text-orange-600'}`}>
                                {quedaSaldada ? '✓ Queda saldada' : `Quedará: ${formatMoney(saldoFinal, f.moneda)}`}
                              </p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notas / Comentario</label>
                  <textarea
                    value={ncNotas}
                    onChange={(e) => setNCNotas(e.target.value)}
                    placeholder="Ej: Aplicación de NC por..."
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                    rows={2}
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl shrink-0 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-xs flex-wrap">
                  <div><span className="text-gray-500">Se aplica: </span><span className="font-semibold text-green-600">{formatMoney(totalAplicar, selectedNC.moneda)}</span> <span className="text-gray-400">a {seleccionadas.length} fact.</span></div>
                  <span className="text-gray-400">→</span>
                  <div><span className="text-gray-500">Sobrante NC: </span><span className={`font-bold ${sobranteNC > 0.01 ? 'text-orange-600' : 'text-green-600'}`}>{formatMoney(sobranteNC, selectedNC.moneda)}</span></div>
                  {sobranteNC > 0.01 && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 font-medium rounded-full">Queda disponible</span>}
                </div>
                <div className="flex gap-2">
                  <button onClick={cerrar} className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm">Cancelar</button>
                  <button
                    onClick={async () => {
                      setAplicandoNC(true);
                      try {
                        const res = await gastosApi.aplicarNC(selectedNC.id, selectedFENCIds, ncNotas);
                        const d = res.data.data;
                        toast.success(`NC aplicada: ${formatMoney(d.totalAplicado, selectedNC.moneda)} a ${d.facturasAplicadas} factura(s)${d.creditoRestanteNC > 0.01 ? ` · sobrante ${formatMoney(d.creditoRestanteNC, selectedNC.moneda)}` : ''}`);
                        cerrar();
                        setSelectedFacturas([]);
                        loadFacturas();
                      } catch (err) {
                        toast.error(err.response?.data?.error || 'Error al aplicar NC');
                      } finally {
                        setAplicandoNC(false);
                      }
                    }}
                    disabled={aplicandoNC || selectedFENCIds.length === 0 || totalAplicar <= 0}
                    className="px-6 py-2 border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 disabled:opacity-50 font-medium flex items-center gap-2 text-sm"
                  >
                    {aplicandoNC ? <><RefreshCw className="h-4 w-4 animate-spin" /> Aplicando...</> : <><Check className="h-4 w-4" /> Aplicar NC{seleccionadas.length > 1 ? ` (${seleccionadas.length})` : ''}</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal de Pago Múltiple */}
      {showPagoMultipleModal && selectedFacturas.length > 0 && (
        <PagoMultipleModal
          facturas={selectedFacturas}
          proveedor={proveedorSeleccionado}
          bancos={bancos}
          formatMoney={formatMoney}
          onClose={() => setShowPagoMultipleModal(false)}
          onSuccess={(notifInfo) => {
            setShowPagoMultipleModal(false);
            setSelectedFacturas([]);
            loadFacturas();
            if (notifInfo && notifInfo.proveedorId) setNotifPagoData(notifInfo);
          }}
        />
      )}

      {/* Notificación automática de pago al proveedor (se abre tras aplicar) */}
      {notifPagoData && (
        <NotificarPagoProveedorModal
          info={notifPagoData}
          emisor={currentEmisor}
          onClose={() => setNotifPagoData(null)}
        />
      )}
    </div>
  );
}

// Modal de Pago Múltiple para CxP
function PagoMultipleModal({ facturas, proveedor, bancos, formatMoney, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    fechaPago: dayjs().format('YYYY-MM-DD'),
    medioPago: 'transferencia',
    numeroComprobante: '',
    banco: '',
    notas: ''
  });
  // Monto individual por FE — default = saldo pendiente completo
  const [montosPorFE, setMontosPorFE] = useState(() => {
    const m = {};
    facturas.forEach(f => { m[f.id] = (parseFloat(f.saldoPendiente) || 0).toFixed(2); });
    return m;
  });

  const moneda = facturas[0]?.moneda || 'CRC';
  // Sumar saldos redondeados a céntimos para que cuadre con lo que se paga por FE.
  const totalPendiente = facturas.reduce((sum, f) => sum + Math.round((parseFloat(f.saldoPendiente) || 0) * 100) / 100, 0);
  const montoTotalCalc = facturas.reduce((s, f) => s + (parseFloat(montosPorFE[f.id]) || 0), 0);

  const setMontoFE = (id, val) => setMontosPorFE(prev => ({ ...prev, [id]: val }));
  const setMontoFEPct = (id, saldo, pct) => setMontoFE(id, ((saldo || 0) * pct / 100).toFixed(2));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (montoTotalCalc <= 0) {
      toast.error('Ingrese al menos un monto válido por factura');
      return;
    }

    // Validar cada monto individual contra el saldo pendiente
    for (const f of facturas) {
      const m = parseFloat(montosPorFE[f.id]) || 0;
      const saldo = parseFloat(f.saldoPendiente) || 0;
      if (m < 0) {
        toast.error(`Monto negativo en factura ${f.fecNumeroFacturaProveedor || f.numeroConsecutivo}`);
        return;
      }
      if (m > saldo + 0.01) {
        toast.error(`El monto excede el saldo de la factura ${f.fecNumeroFacturaProveedor || f.numeroConsecutivo}`);
        return;
      }
    }

    if (!file) {
      toast.error('Debe adjuntar el comprobante de pago');
      return;
    }

    try {
      setLoading(true);

      // Solo enviar FE con monto > 0
      const montosArr = facturas
        .map(f => ({ facturaId: f.id, monto: parseFloat(montosPorFE[f.id]) || 0 }))
        .filter(x => x.monto > 0);

      const data = new FormData();
      data.append('facturaIds', JSON.stringify(montosArr.map(x => x.facturaId)));
      data.append('montosPorFactura', JSON.stringify(montosArr));
      data.append('fechaPago', formData.fechaPago);
      data.append('montoTotal', montoTotalCalc.toFixed(2));
      data.append('moneda', moneda);
      data.append('medioPago', formData.medioPago);
      data.append('numeroComprobante', formData.numeroComprobante || '');
      data.append('banco', formData.banco || '');
      data.append('notas', formData.notas || '');
      data.append('comprobante', file);

      const res = await gastosApi.aplicarPagoMultiple(data);
      toast.success(`Pago aplicado a ${montosArr.length} factura${montosArr.length !== 1 ? 's' : ''}`);
      const d = res?.data?.data || {};
      const notifInfo = {
        parentId: d.parentId || null,
        pagoId: null,
        proveedorId: d.proveedorId || proveedor?.id || facturas[0]?.proveedorId,
        proveedorNombre: proveedor?.nombre || facturas[0]?.proveedorNombre || '',
        moneda,
        montoTotal: montoTotalCalc,
        fechaPago: formData.fechaPago,
        medioPago: formData.medioPago,
        banco: formData.banco,
        numeroComprobante: formData.numeroComprobante,
        comprobanteFilename: file?.name || null,
        facturasCount: montosArr.length
      };
      onSuccess(notifInfo);
    } catch (error) {
      console.error('Error aplicando pago múltiple:', error);
      toast.error(error.response?.data?.error || 'Error al aplicar pago');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-3xl mx-4 shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-orange-600 to-orange-700 rounded-t-xl">
          <h3 className="text-lg font-semibold text-white">Pago Múltiple — {facturas.length} Facturas</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Proveedor Info */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold text-gray-900">{proveedor?.nombre}</p>
              <p className="text-sm text-gray-500">{proveedor?.identificacion}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Total a Pagar</p>
              <p className="text-2xl font-bold text-orange-700 ">
                {formatMoney(montoTotalCalc, moneda)}
              </p>
              {Math.abs(montoTotalCalc - totalPendiente) > 0.01 && (
                <p className="text-[11px] text-gray-500 mt-0.5">
                  de {formatMoney(totalPendiente, moneda)} pendiente
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Facturas List — montos editables individuales */}
        <div className="px-6 py-3 border-b max-h-72 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500">Facturas Seleccionadas</p>
            <p className="text-[11px] text-gray-400">Editá el monto de cada FE si pagás solo parte</p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] text-gray-500 uppercase">
                <th className="text-left font-medium pb-1.5">Factura</th>
                <th className="text-right font-medium pb-1.5">Saldo</th>
                <th className="text-right font-medium pb-1.5 w-36">A pagar</th>
                <th className="text-right font-medium pb-1.5 w-28">Saldo final</th>
              </tr>
            </thead>
            <tbody>
              {facturas.map(f => {
                const saldo = parseFloat(f.saldoPendiente) || 0;
                const aplicado = parseFloat(montosPorFE[f.id]) || 0;
                const saldoFinal = Math.max(0, Math.round((saldo - aplicado) * 100) / 100);
                const quedaSaldada = aplicado > 0 && saldoFinal <= 0.01;
                const excede = aplicado > saldo + 0.01;
                return (
                  <tr key={f.id} className="border-t border-gray-100">
                    <td className="py-1.5 font-mono text-xs text-gray-700">
                      {f.fecNumeroFacturaProveedor || f.numeroConsecutivo || f.numeroFactura || f.consecutivo}
                    </td>
                    <td className="py-1.5 text-right text-gray-600">{formatMoney(saldo, f.moneda)}</td>
                    <td className="py-1.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max={saldo}
                          value={montosPorFE[f.id]}
                          onChange={(e) => setMontoFE(f.id, e.target.value)}
                          className={`w-28 border rounded px-2 py-1 text-right text-sm font-mono focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${excede ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                        />
                        <button
                          type="button"
                          onClick={() => setMontoFEPct(f.id, saldo, 100)}
                          className="text-[10px] px-1.5 py-1 border border-gray-300 rounded text-gray-600 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-700"
                          title="Pagar total"
                        >
                          100%
                        </button>
                      </div>
                    </td>
                    <td className={`py-1.5 text-right font-semibold ${quedaSaldada ? 'text-emerald-600' : aplicado > 0 ? 'text-orange-600' : 'text-gray-300'}`}>
                      {aplicado > 0 ? (quedaSaldada ? '✓ Saldada' : formatMoney(saldoFinal, f.moneda)) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Pago *
              </label>
              <input
                type="date"
                value={formData.fechaPago}
                onChange={(e) => setFormData({ ...formData, fechaPago: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medio de Pago
              </label>
              <select
                value={formData.medioPago}
                onChange={(e) => setFormData({ ...formData, medioPago: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="cheque">Cheque</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="sinpe">SINPE Móvil</option>
                <option value="otro">Otro</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                N° Comprobante / Referencia
              </label>
              <input
                type="text"
                value={formData.numeroComprobante}
                onChange={(e) => setFormData({ ...formData, numeroComprobante: e.target.value })}
                placeholder="Ej: 123456"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Banco
              </label>
              <select
                value={formData.banco}
                onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">-- Seleccionar --</option>
                {bancos.map(b => (
                  <option key={b.id} value={b.nombre}>{b.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comprobante *
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full border rounded-lg px-2 py-1.5 text-sm file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
              required
            />
            {!file && (
              <p className="text-xs text-amber-600 mt-1">Debe adjuntar el comprobante de pago</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !file || montoTotalCalc <= 0}
              className="px-6 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 font-medium flex items-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></span>
                  Procesando...
                </>
              ) : (
                <>
                  <Check size={18} />
                  Aplicar Pago {formatMoney(montoTotalCalc, moneda)}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
